import { useState } from 'react'
import {
  SparePartAvailabilities,
  type SparePart,
  type SparePartsCatalog,
  type SparePartAvailability,
} from '@esf/shared'

interface SparePartsFormProps {
  catalog: SparePartsCatalog | null
  onChange: (catalog: SparePartsCatalog) => void
  disabled?: boolean
}

const AvailabilityLabels: Record<SparePartAvailability, string> = {
  IN_STOCK: 'In Stock',
  MADE_TO_ORDER: 'Made to Order',
  DISCONTINUED: 'Discontinued',
  COMING_SOON: 'Coming Soon',
}

const AvailabilityColors: Record<SparePartAvailability, string> = {
  IN_STOCK: 'bg-green-100 text-green-800',
  MADE_TO_ORDER: 'bg-blue-100 text-blue-800',
  DISCONTINUED: 'bg-red-100 text-red-800',
  COMING_SOON: 'bg-yellow-100 text-yellow-800',
}

const emptyPart: SparePart = {
  partNumber: '',
  name: '',
  gtin: '',
  availability: 'IN_STOCK',
  availabilityEndDate: '',
  priceEur: 0,
  priceGbp: 0,
  deliveryTimeDays: 7,
  installationDifficultyLevel: 1,
}

const defaultCatalog: SparePartsCatalog = {
  parts: [],
  catalogVersion: '1.0',
  lastUpdated: new Date().toISOString().split('T')[0],
}

export default function SparePartsForm({
  catalog,
  onChange,
  disabled,
}: SparePartsFormProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const data = catalog || defaultCatalog

  const updateField = <K extends keyof SparePartsCatalog>(
    field: K,
    value: SparePartsCatalog[K]
  ) => {
    onChange({ ...data, [field]: value })
  }

  const addPart = () => {
    const newParts = [...data.parts, { ...emptyPart }]
    onChange({ ...data, parts: newParts, lastUpdated: new Date().toISOString().split('T')[0] })
    setExpandedIndex(newParts.length - 1)
  }

  const removePart = (index: number) => {
    const newParts = data.parts.filter((_, i) => i !== index)
    onChange({ ...data, parts: newParts, lastUpdated: new Date().toISOString().split('T')[0] })
    if (expandedIndex === index) {
      setExpandedIndex(null)
    }
  }

  const updatePart = (index: number, field: keyof SparePart, value: string | number) => {
    const newParts = data.parts.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    )
    onChange({ ...data, parts: newParts, lastUpdated: new Date().toISOString().split('T')[0] })
  }

  // Calculate minimum availability date (5 years from now per ESPR)
  const minAvailabilityDate = new Date()
  minAvailabilityDate.setFullYear(minAvailabilityDate.getFullYear() + 5)
  const minDateStr = minAvailabilityDate.toISOString().split('T')[0]

  return (
    <div className="space-y-6">
      {/* Catalog Metadata */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Spare Parts Catalog</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Catalog Version</label>
            <input
              type="text"
              value={data.catalogVersion}
              onChange={e => updateField('catalogVersion', e.target.value)}
              disabled={disabled}
              className="filter-input w-full text-sm"
              placeholder="1.0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Last Updated</label>
            <input
              type="date"
              value={data.lastUpdated?.split('T')[0] || ''}
              onChange={e => updateField('lastUpdated', e.target.value)}
              disabled={disabled}
              className="filter-input w-full text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Total Parts</label>
            <p className="text-lg font-bold text-gray-900">{data.parts.length}</p>
          </div>
        </div>
      </div>

      {/* ESPR Compliance Note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex gap-2">
          <svg className="h-5 w-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-800">ESPR Article 5 - Spare Parts Availability</p>
            <p className="text-xs text-amber-700 mt-1">
              Spare parts must be available for <strong>minimum 5 years</strong> after last unit placed on market.
              Price transparency and reasonable delivery times required.
            </p>
          </div>
        </div>
      </div>

      {/* Parts List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-medium text-gray-700">Spare Parts List</h4>
            <p className="text-xs text-gray-500">Repair-enabling parts with pricing and availability</p>
          </div>
          <button
            type="button"
            onClick={addPart}
            disabled={disabled}
            className="btn btn-secondary text-sm py-1 px-3"
          >
            + Add Part
          </button>
        </div>

        {data.parts.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No spare parts defined</p>
            <p className="text-xs text-gray-400">Add parts to enable repair transparency</p>
          </div>
        ) : (
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Price (EUR)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Delivery</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.parts.map((part, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div
                        className="cursor-pointer"
                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                      >
                        <p className="text-sm font-medium text-gray-900">{part.name || 'Unnamed part'}</p>
                        <p className="text-xs text-gray-500">{part.partNumber || 'No part number'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${AvailabilityColors[part.availability]}`}>
                        {AvailabilityLabels[part.availability]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">
                      {part.priceEur ? `€${part.priceEur.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-500">
                      {part.deliveryTimeDays ? `${part.deliveryTimeDays} days` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                        className="text-esf-navy hover:text-esf-navy/70 text-sm font-medium mr-2"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => removePart(index)}
                        disabled={disabled}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Expanded Edit Form */}
            {expandedIndex !== null && (
              <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
                <h5 className="text-sm font-medium text-gray-700 mb-3">
                  Editing: {data.parts[expandedIndex]?.name || 'New Part'}
                </h5>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                    <input
                      type="text"
                      value={data.parts[expandedIndex]?.partNumber || ''}
                      onChange={e => updatePart(expandedIndex, 'partNumber', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full"
                      placeholder="e.g., ESF-ARM-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Part Name</label>
                    <input
                      type="text"
                      value={data.parts[expandedIndex]?.name || ''}
                      onChange={e => updatePart(expandedIndex, 'name', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full"
                      placeholder="e.g., Armrest replacement"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GTIN</label>
                    <input
                      type="text"
                      value={data.parts[expandedIndex]?.gtin || ''}
                      onChange={e => updatePart(expandedIndex, 'gtin', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full"
                      placeholder="14-digit GTIN"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                    <select
                      value={data.parts[expandedIndex]?.availability || 'IN_STOCK'}
                      onChange={e => updatePart(expandedIndex, 'availability', e.target.value)}
                      disabled={disabled}
                      className="filter-select w-full"
                    >
                      {SparePartAvailabilities.map(status => (
                        <option key={status} value={status}>{AvailabilityLabels[status]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Available Until</label>
                    <input
                      type="date"
                      value={data.parts[expandedIndex]?.availabilityEndDate?.split('T')[0] || ''}
                      onChange={e => updatePart(expandedIndex, 'availabilityEndDate', e.target.value)}
                      disabled={disabled}
                      className="filter-input w-full"
                      min={minDateStr}
                    />
                    <p className="text-xs text-gray-500 mt-1">Min 5 years required</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (EUR)</label>
                    <input
                      type="number"
                      value={data.parts[expandedIndex]?.priceEur || ''}
                      onChange={e => updatePart(expandedIndex, 'priceEur', parseFloat(e.target.value) || 0)}
                      disabled={disabled}
                      className="filter-input w-full"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (GBP)</label>
                    <input
                      type="number"
                      value={data.parts[expandedIndex]?.priceGbp || ''}
                      onChange={e => updatePart(expandedIndex, 'priceGbp', parseFloat(e.target.value) || 0)}
                      disabled={disabled}
                      className="filter-input w-full"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Time (days)</label>
                    <input
                      type="number"
                      value={data.parts[expandedIndex]?.deliveryTimeDays || ''}
                      onChange={e => updatePart(expandedIndex, 'deliveryTimeDays', parseInt(e.target.value) || 0)}
                      disabled={disabled}
                      className="filter-input w-full"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Installation Difficulty (1-5)</label>
                    <select
                      value={data.parts[expandedIndex]?.installationDifficultyLevel || 1}
                      onChange={e => updatePart(expandedIndex, 'installationDifficultyLevel', parseInt(e.target.value))}
                      disabled={disabled}
                      className="filter-select w-full"
                    >
                      <option value={1}>1 - Easy (no tools)</option>
                      <option value={2}>2 - Basic tools</option>
                      <option value={3}>3 - Moderate skill</option>
                      <option value={4}>4 - Professional recommended</option>
                      <option value={5}>5 - Professional required</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={() => setExpandedIndex(null)}
                    className="btn btn-secondary text-sm"
                  >
                    Done Editing
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
